import {
    faCheck,
    faCheckCircle,
    faChevronLeft,
    faChevronRight,
    faClipboard,
    faEdit,
    faExclamationCircle,
    faHashtag,
    faInfoCircle,
    faQuestion,
    faRuler,
    faSpellCheck,
    faTimes,
    faUserLock,
} from "@fortawesome/free-solid-svg-icons";
import { faThermometerEmpty } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import { Button, Divider, Input, Modal, Tooltip } from "antd";
import { getRawApiImage } from "components/RawImageComponent";
import AssetIDHelper from "components/util/AssetIDHelper";
import ELRMileAndChain from "components/util/ELRMileAndChain";
import { LazyTippy } from "components/util/LazyTooltips";
import OBCSpinner from "components/util/OBC";
import { convertToTimezone, convertTsToMilliseconds } from "components/util/TimezoneUtils";
import _ from "lodash";
import moment from "moment";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ContentLoader from "react-content-loader";
import Highlighter from "react-highlight-words";
import { useDispatch, useSelector } from "react-redux";
import { areEqual, FixedSizeList } from "react-window";
import { customAudit, getAssetData, goToBounds, reviewExternalObservation, selectAsset, selectIssue, selectObservation, updateIssue } from "redux/actions";
import { sticky } from "tippy.js";
import { createConcensusFields, createGroundTruthFields, formatMovementInterval, StatusElement } from "./issueUtils";
import { asyncLoadImageWithRange } from "components/util/PlaylistUtils";
import { MEMOIZED_DOMAIN_URL } from "components/util/HostUtils";

const classificationTypeIcons = {
    Yes: { icon: faCheck, style: { color: "#479ff8", transform: "translateY(1px)" } },
    No: { icon: faTimes, style: { color: "#479ff8", fontSize: 16 } },
    Uncertain: { icon: faQuestion, style: { color: "#e8dfff66" } },
    Unknown: { icon: faQuestion, style: { color: "#e8dfff66" } },
};

const assetObservationsSelector = (state) => state.mlAssets.observations;
const userConfigSelector = (state) => state.userDetails.userConfig;
const assetGroupsSelector = (state) => state.mlAssets.groups;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const csrfTokenSelector = (state) => state.csrfToken;
const currentDashboardSelector = (state) => _.find(state.dashboards, (dashboard) => dashboard.access_token === state.access_token);
const superUserSelector = (state) => state.userDetails.userConfig.super_admin;
const objectObservationDataTypesSelector = (state) => state.objectObservationDataTypes;

const EMPTY_DICT = {};
const GUTTER_SIZE = 5;

const AssetSkeleton = ({ minHeight, assetRef }) => {
    return (
        <div
            ref={assetRef}
            className={"IssueListItem Closed"}
            style={{ minHeight }}>
            <ContentLoader
                speed={2}
                width={"100%"}
                height={"100%"}
                viewBox="0 0 733 220"
                backgroundColor="#5a608a"
                foregroundColor="#4b5175">
                <rect
                    x="536"
                    y="89"
                    width="179"
                    height="108"
                    rx="3"
                    ry="3"
                />
                <rect
                    x="635"
                    y="15"
                    width="80"
                    height="25"
                    rx="3"
                    ry="3"
                />
                <rect
                    x="26"
                    y="15"
                    width="188"
                    height="25"
                    rx="3"
                    ry="3"
                />
                <rect
                    x="26"
                    y="54"
                    width="119"
                    height="25"
                    rx="3"
                    ry="3"
                />
                <rect
                    x="26"
                    y="93"
                    width="314"
                    height="25"
                    rx="3"
                    ry="3"
                />
                <rect
                    x="26"
                    y="132"
                    width="280"
                    height="65"
                    rx="3"
                    ry="3"
                />
            </ContentLoader>
        </div>
    );
};

const ExternalObservationItem = ({ observation, setParentSize }) => {
    const dispatch = useDispatch();
    const assetObservations = useSelector(assetObservationsSelector);

    useEffect(() => {
        setParentSize();
    }, []);

    const verifyExternalObservation = (e) => {
        e.stopPropagation();
        dispatch(reviewExternalObservation(observation.id, observation.review_status === 0, assetObservations));
    };

    let asset_id = observation.asset_id;
    let description = observation.description;
    let elr = observation.route_position_elr;
    let chains = observation.route_position_chains;
    let trid = observation.route_position_trid;
    let item_lat_lng = `${parseFloat(observation.lat.toFixed(6))}, ${parseFloat(observation.lng.toFixed(6))}`;

    // TODO this audit various places that use explicit coord system like this - buggy?
    let line_ref = ELRMileAndChain.from_fields("ELR Mile & Chain", "elr_meterage", {
        ELR: elr,
        METERAGE: chains * 20.1168,
        TRACK: trid,
    });

    let item_text = `${asset_id}`;

    if (description) {
        item_text += ` (${description})`;
    }

    if (line_ref && line_ref.elr) {
        item_text += ` at ${line_ref.to_mile_and_yard_string()}`;
    }
    const verifyStatus = observation.review_status ? (
        <button
            className="ExternalAssetVerify"
            onClick={verifyExternalObservation}>
            <FontAwesomeIcon
                icon={faCheckCircle}
                className="ExternalAssetIconBlue"
            />
            Verified
        </button>
    ) : (
        <button
            className="ExternalAssetVerify"
            onClick={verifyExternalObservation}>
            <FontAwesomeIcon
                icon={faCheckCircle}
                className="ExternalAssetIconGreen"
            />
            Verify observation
        </button>
    );

    return (
        <div class="ObservationItemContainer">
            <div className="ObservationItem">
                <span className="ID">#{observation.id}</span>
                <span className="SessionID">
                    <b>External Record</b>
                </span>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span className="DateAndTime">{item_text}</span>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <span style={{ textAlign: "center", marginRight: "0.5em" }}>{item_lat_lng}</span>
                        <LazyTippy
                            placement="top"
                            sticky={true}
                            plugins={[sticky]}
                            arrow={true}
                            theme="aivrlight"
                            interactive="true"
                            content="Copy Lat / Lon data to clipboard">
                            <div
                                className="CopyToClipboard"
                                onClick={() => {
                                    navigator.clipboard.writeText(item_lat_lng);
                                }}>
                                <FontAwesomeIcon icon={faClipboard} />
                            </div>
                        </LazyTippy>
                    </div>
                </div>
                <div>{verifyStatus}</div>
            </div>
        </div>
    );
};

const ObservationItem = ({ observation, setParentSize, showFailedMatches, userConfig }) => {
    const dispatch = useDispatch();
    const currentDashboard = useSelector(currentDashboardSelector);
    const csrfToken = useSelector(csrfTokenSelector);

    const [imageLoading, setImageLoading] = useState(true);
    const [observationImageSrc, setObservationImageSrc] = useState(null);

    const onClick = (e) => {
        e.stopPropagation();
        dispatch(selectObservation(observation.id));
    };

    const formattedImageUrl = useMemo(() => {
        if (observation.image_url && csrfToken) {
            let imgUrl = observation.image_url;

            // remove frame index if session is from before 01/05/2020
            if (Math.round(observation.timestamp / 1000) <= 1588287599) {
                const arr = imgUrl.split("");
                const lastSlash = _.findLastIndex(arr, (e) => e === "/");
                const lastIndex = _.findLastIndex(arr, (e) => e === "-");
                if (lastIndex > lastSlash) {
                    return arr.slice(0, lastIndex).join("") + ".jpg";
                }
            }

            return `${imgUrl}?csrf=${csrfToken}`;
        }

        return null;
    }, [csrfToken, observation.image_url, observation.timestamp]);

    useEffect(() => {
        if (!observationImageSrc) {
            const callback = (imageSrc) => {
                setImageLoading(false);
                if (imageSrc) {
                    setObservationImageSrc(imageSrc);
                }
            };

            if ("stills_stride" in observation && _.isNumber(observation.stills_stride) && observation.stills_stride > 0) {
                const url = `https://raw${MEMOIZED_DOMAIN_URL}/${observation.device_key}/${observation.session_key}/${observation.video_key}.stills.txt`;

                const startRange = observation.stills_stride * (observation.frame - 1);
                const endRange = startRange + observation.stills_stride - 1;

                asyncLoadImageWithRange(url, { start: startRange, end: endRange }, csrfToken).then(callback);
            } else if (formattedImageUrl) {
                getRawApiImage(formattedImageUrl, currentDashboard.access_token, callback);
            }
        }
    }, [csrfToken, currentDashboard.access_token, formattedImageUrl, observation, observationImageSrc]);

    const humanDate = useMemo(() => {
        const dateTime = new Date(convertTsToMilliseconds(observation.timestamp));
        return convertToTimezone(dateTime, userConfig.convert_to_utc, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    }, [observation.timestamp, userConfig.convert_to_utc]);

    const infoItems = useMemo(() => {
        const observationInfo = observation.observation_info;
        const infoItems = [];

        if (!observationInfo) {
            return [];
        }
        if (observationInfo.best_temp) {
            infoItems.push(<span key="temp">{observationInfo.best_temp} Degrees</span>);
        }

        if (observationInfo.gap_mm) {
            infoItems.push(<span key="gap">{observationInfo.gap_mm}mm</span>);
        }
        return infoItems;
    }, [observation.observation_info]);

    const observationImage = useMemo(() => {
        if (imageLoading) {
            return (
                <OBCSpinner
                    size={50}
                    speed={3}
                    colorScheme={"mono"}
                />
            );
        }

        if (observationImageSrc) {
            return (
                <img
                    isLoading={imageLoading}
                    src={observationImageSrc}
                    onLoad={setParentSize}
                    alt="Observation"
                    crossOrigin="anonymous"
                    loading="lazy"
                />
            );
        }

        return null;
    }, [imageLoading, observationImageSrc, setParentSize]);

    return (
        <div class="ObservationItemContainer">
            <div
                className="ObservationItem"
                id="ObservationItem"
                onClick={onClick}>
                <span className="ID">
                    #{observation.id}
                    {observation.matching_state === 1 && (
                        <Tippy
                            arrow={true}
                            placement="top"
                            content={"Matched On Consensus"}
                            theme="aivr"
                            zIndex="1000"
                            delay="100">
                            <div className="MatchingIcon">
                                <FontAwesomeIcon
                                    icon={faSpellCheck}
                                    className="MatchingIcon"
                                />
                            </div>
                        </Tippy>
                    )}
                    {observation.matching_state === 9 && (
                        <Tippy
                            arrow={true}
                            placement="top"
                            content={"Force Matched"}
                            theme="aivr"
                            zIndex="1000"
                            delay="100">
                            <div className="MatchingIcon">
                                <FontAwesomeIcon
                                    icon={faUserLock}
                                    className="MatchingIcon"
                                />
                            </div>
                        </Tippy>
                    )}
                    {[88, 89].includes(observation.matching_state) && (
                        <Tippy
                            arrow={true}
                            placement="top"
                            content={"Not Matched"}
                            theme="aivr"
                            zIndex="1000"
                            delay="100">
                            <div className="MatchingIcon">
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="MatchingIcon"
                                />
                            </div>
                        </Tippy>
                    )}
                </span>
                <span className="SessionID">
                    Session:{" "}
                    <span style={{ marginLeft: 5 }}>
                        <b>#{observation.session_id}</b>
                    </span>
                </span>
                <span className="DateAndTime">{humanDate}</span>
                {infoItems}
                <div className="ObservationImageContainer">
                    <Tippy
                        arrow={true}
                        placement="bottom"
                        content={<div className="IssueImageContainerToolTip">{observationImage}</div>}
                        theme="aivr"
                        zIndex="1000"
                        delay="100">
                        {observationImage}
                    </Tippy>
                </div>
            </div>
        </div>
    );
};

const AssetItem = ({ index, assetID, searchQuery, autoAck, setSize, windowWidth, selected, minHeight, showFailedMatches }) => {
    const dispatch = useDispatch();

    const [notes, setNotes] = useState("");
    const [originalNotes, setOriginalNotes] = useState("");
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [assetImageSrc, setAssetImageSrc] = useState(null);
    const [assetImageSrcTwo, setAssetImageSrcTwo] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageTwoLoading, setImageTwoLoading] = useState(true);
    const [displayExternalAsset, setDisplayExternalAsset] = useState(false);
    const [externalAssetNumber, setExternalAssetNumber] = useState(0);
    const [copyLinkTooltip, setCopyLinkTooltip] = useState("Copy link to asset");

    const assetObservations = useSelector(assetObservationsSelector);
    const objectObservationDataTypes = useSelector(objectObservationDataTypesSelector);
    const userConfig = useSelector(userConfigSelector);
    const assetGroups = useSelector(assetGroupsSelector);
    const assetData = useSelector((state) => state.mlAssets.assetData[assetID] || EMPTY_DICT);
    const elrUnits = useSelector(elrUnitsSelector);
    const isSuperUser = useSelector(superUserSelector);
    const currentDashboard = useSelector(currentDashboardSelector);
    const shouldShowVeg = _.get(currentDashboard, ["config", "show_ohl_veg"], true);

    const ref = useRef();

    let observations = null;
    let external_observations = null;
    observations = assetObservations.observations;
    external_observations = assetObservations.external_observations;

    const hasOnlyExternalObservations = useMemo(() => {
        if (_.get(assetData, "summary_data.match_count") === 0 && _.get(assetData, "summary_data.external_match_count") > 0) {
            return true;
        }
        return false;
    }, [assetData]);

    const setIssueSize = () => {
        if (ref.current) {
            const size = ref.current.getBoundingClientRect().height - 20;
            setSize(index, size);
        }
    };

    useEffect(() => {
        dispatch(getAssetData(assetID)).catch((error) => {
            console.log("debug error getting asset data", error);
        });
    }, [assetID, dispatch]);

    useEffect(() => {
        setTimeout(() => {
            setIssueSize();
        }, 100);
    }, [setSize, index, windowWidth, selected, observations, assetData]);

    useEffect(() => {
        if (notesModalOpen) {
            let note_value = assetData.note ? assetData.note : "";
            setNotes(note_value);
            setOriginalNotes(note_value);
        }
    }, [notesModalOpen]);

    const humanReadableDate = useMemo(() => {
        if (assetData.summary_data && assetData.summary_data.last_observed) {
            const dateTime = new Date(convertTsToMilliseconds(assetData.summary_data.last_observed));
            return convertToTimezone(dateTime, userConfig.convert_to_utc);
        } else {
            return null;
        }
    }, [assetData.summary_data, userConfig.convert_to_utc]);

    const positionDifference = useMemo(() => {
        return _.get(assetData, "summary_data.fields.external_pos_diff");
    }, [assetData]);

    const onIssueItemClick = (evt, openModal = false) => {
        setLoading(true);
        if (
            [
                "StatusContainer",
                "StatusContainer Clickable",
                "StatusContainer ant-popover-open",
                "ant-btn ant-btn-sm",
                "ant-btn ant-btn-primary ant-btn-sm",
            ].includes(evt.target?.className)
        ) {
            return;
        }
        if (selected) {
            if (_.isEmpty(evt)) {
                if (openModal && observations && observations.length) {
                    dispatch(selectObservation(observations[0].id));
                }
            } else {
                dispatch(selectIssue(null));
                dispatch(selectAsset(null));
            }
        } else {
            dispatch(selectAsset(assetData.id, showFailedMatches)).then((observations) => {
                if (openModal && observations && observations.length) {
                    dispatch(selectObservation(observations[0].id));
                }
            });

            let north = assetData["coords"][1] + 0.002;
            let east = assetData["coords"][0] + 0.002;
            let south = assetData["coords"][1] - 0.002;
            let west = assetData["coords"][0] - 0.002;
            dispatch(
                goToBounds({
                    north,
                    south,
                    east,
                    west,
                }),
            );
            const assetType = _.get(assetData, "class", "");
            dispatch(
                customAudit(
                    "asset_item_click",
                    { asset_type: assetType, target: "Asset Tab", source: "mouse" },
                    `asset_item_click ${assetType} clicked from the Assets tab`,
                ),
            );
        }
    };

    useEffect(() => {
        if (!_.isNil(observations) || !_.isNil(external_observations)) {
            setLoading(false);
        }
    }, [observations, external_observations]);

    const filteredObservations = useMemo(() => {
        if (!observations) {
            return [];
        }

        return observations.filter((obs) => {
            return !obs.classification || obs.classification === "verified";
        });
    }, [observations]);

    const orderedObservations = useMemo(() => {
        return _.orderBy(filteredObservations, "timestamp", "desc");
    }, [filteredObservations]);

    const ObservationItemRow = memo(({ index, style }) => {
        if (index < orderedObservations.length) {
            const observation = orderedObservations[index];
            return (
                <div style={style}>
                    <ObservationItem
                        key={observation.id}
                        setParentSize={setIssueSize}
                        observation={observation}
                        userConfig={userConfig}
                    />
                </div>
            );
        }

        if (external_observations.length > 0) {
            const externalObservationsIndex = index - orderedObservations.length;
            const externalObservation = external_observations[externalObservationsIndex];
            return (
                <div style={style}>
                    <ExternalObservationItem
                        key={"ext-" + externalObservation.id}
                        setParentSize={setIssueSize}
                        observation={externalObservation}
                    />
                </div>
            );
        }

        return null;
    }, areEqual);

    const observationItems = useMemo(() => {
        if (loading) {
            return (
                <div className="SessionSpinner">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            );
        }

        if (!selected) {
            return [];
        }

        if (orderedObservations && external_observations && !orderedObservations.length && external_observations.length === 0) {
            return <p className="NoObservationsText">No Observations Found</p>;
        }

        const totalItems = orderedObservations.length + (external_observations ? external_observations.length : 0);
        const itemSize = 120 + GUTTER_SIZE;
        const height = Math.min(300, GUTTER_SIZE + itemSize * totalItems);

        return (
            <div
                className="ObservationsList"
                onClick={(e) => e.stopPropagation()}>
                <FixedSizeList
                    itemCount={totalItems}
                    itemSize={itemSize}
                    height={height}
                    width={"100%"}>
                    {ObservationItemRow}
                </FixedSizeList>
            </div>
        );
    }, [loading, selected, orderedObservations, external_observations, ObservationItemRow]);

    const openNotesModal = (e) => {
        e.stopPropagation();
        setNotesModalOpen(true);
    };

    const saveNotes = () => {
        let update = { note: notes };
        if (autoAck && assetData.state === 0) {
            update.state = 1;
        }
        dispatch(updateIssue(assetData.id, update, false));
        setOriginalNotes(notes);
        setNotesModalOpen(false);
    };

    const handleCancel = () => {
        setNotesModalOpen(false);
        setNotes(originalNotes);
    };

    const convertDate = (date) => {
        const newDate = new Date(convertTsToMilliseconds(date));
        return convertToTimezone(newDate, userConfig.convert_to_utc);
    };

    const openGroundTruthModal = (e) => {
        e.stopPropagation();
        onIssueItemClick({}, true);
    };

    const onCopyLinkClicked = useCallback(
        (e) => {
            e.stopPropagation();
            const hostname = window.location.hostname;
            const port = window.location.port ? `:${window.location.port}` : "";
            const protocol = window.location.protocol;
            const assetLink = `${protocol}//${hostname}${port}/asset/${assetData.id}`;

            navigator.clipboard
                .writeText(assetLink)
                .then(() => {
                    setCopyLinkTooltip("Copied!");
                })
                .catch(() => {
                    // Fallback for browsers that don't support clipboard API
                    setCopyLinkTooltip("Copy failed");
                });
        },
        [assetData.id],
    );

    const observationsWorkspaceWarning = useMemo(() => {
        if (!selected || loading || !observations) return null;

        const issueCount = _.get(assetData, "summary_data.match_count", 0);

        if (issueCount === observations.length) return null;

        return (
            <Tippy content="Some observations of this asset are not visible in this workspace">
                <div style={{ display: "inline" }}>
                    <FontAwesomeIcon
                        icon={faExclamationCircle}
                        style={{ color: "#e55151", fontSize: "16px", cursor: "pointer" }}
                    />
                </div>
            </Tippy>
        );
    }, [assetData, observations, selected, loading]);

    const infoFields = useMemo(() => {
        const fields = _.get(assetData, ["summary_data", "fields"], {});

        const infoElements = [];

        let match_count = _.get(assetData, ["summary_data", "match_count"], false);
        if (match_count) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="match_info">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faHashtag}
                    />
                    <p className="IssueInfoText">
                        <b>Observations</b>: {match_count} (Last seen {humanReadableDate}) {observationsWorkspaceWarning}
                    </p>
                </div>,
            );
        } else {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="match_info">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faHashtag}
                    />
                    <p className="IssueInfoText">
                        <b>Last Observation</b>: {humanReadableDate}
                    </p>
                </div>,
            );
        }

        if (isSuperUser && !_.isEmpty(assetData)) {
            const failedObservationCount = _.get(assetData, "failed_observation_count");
            const totalObservationCount = _.get(assetData, "total_observation_count");
            if (_.isNumber(failedObservationCount) && _.isNumber(totalObservationCount) && totalObservationCount > 0) {
                infoElements.push(
                    <div
                        className="IssueInfo"
                        key="fail_count">
                        <p className="IssueInfoText">
                            <b>Failed observations</b>: {Math.round((failedObservationCount / totalObservationCount) * 100)}%
                        </p>
                    </div>,
                );
            }
        }

        if (fields.last_temperature) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="temp">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faThermometerEmpty}
                    />
                    <p className="IssueInfoText">
                        <b>Last Temperature</b>: {fields.last_temperature} Degrees
                    </p>
                </div>,
            );
        }

        if (fields.highest_temperature) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="max_temp">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faThermometerEmpty}
                    />
                    <p className="IssueInfoText">
                        <b>Highest Temperature</b>: {fields.highest_temperature} Degrees
                    </p>
                </div>,
            );
        }

        if (fields.last_gap_mm) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="gap">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Last Gap</b>: {fields.last_gap_mm}mm
                    </p>
                </div>,
            );
        }

        if (fields.max_gap_mm) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="max_gap">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Largest Gap</b>: {fields.max_gap_mm}mm
                    </p>
                </div>,
            );
        }

        if (fields.min_gap_mm) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="min_gap">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Smallest Gap</b>: {fields.min_gap_mm}mm
                    </p>
                </div>,
            );
        }

        if (fields.last_height_diff_px) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="last_height_diff_px">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Last Height Difference</b>: {fields.last_height_diff_px}px
                    </p>
                </div>,
            );
        }

        if (fields.bottom_out_temperature) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="bottom_out_temp">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faThermometerEmpty}
                    />
                    <p className="IssueInfoText">
                        <b>Latest Bottom Out Temperature</b>:{" "}
                        {fields.bottom_out_temperature > 50 ? `50°C+ (${fields.bottom_out_temperature}°C)` : `${fields.bottom_out_temperature}°C`}
                    </p>
                </div>,
            );
        }

        if (_.get(assetData, "class") === "Tensioner") {
            const mpd = _.get(assetData, ["ground_truth_data", "midpoint_distance_meters"], null);

            infoElements.push(
                <div className="IssueInfoGroup">
                    {["30d", "60d"].map((interval) => {
                        const formattedInterval = formatMovementInterval(fields, interval, mpd);

                        return (
                            <div
                                className="IssueInfo"
                                style={{ display: "flex", alignItems: "center" }}
                                key={interval}>
                                <FontAwesomeIcon
                                    className="InfoIcon"
                                    icon={faRuler}
                                />
                                <div className="IssueInfoText">
                                    <span>{`${interval} movement`}: </span>
                                    {formattedInterval.parts.map((part, index) => (
                                        <React.Fragment key={index}>
                                            {index > 0 && " • "}
                                            {part}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <span style={{ display: "flex", alignItems: "center", marginLeft: 12 }}>{formattedInterval.status}</span>
                            </div>
                        );
                    })}
                </div>,
            );
        }

        if (fields.severity) {
            infoElements.push(
                <div
                    className="IssueInfo"
                    key="severity">
                    <FontAwesomeIcon
                        className="InfoIcon"
                        icon={faRuler}
                    />
                    <p className="IssueInfoText">
                        <b>Green zone overflow</b>: {fields.severity}cm
                    </p>
                </div>,
            );
        }

        const assetDataClass = _.get(assetData, "class");

        if (assetDataClass === "Signal") {
            const classificationTypes = _.get(objectObservationDataTypes, "Signal", {});

            const classifications = [];

            Object.keys(classificationTypes).forEach((key) => {
                const isIsolationField = key.includes("isolation");

                if (isIsolationField && !_.get(currentDashboard, ["config", "show_essow_input"], false)) {
                    return;
                }

                const field = _.get(fields, key, "uncertain");
                const classification = _.get(classificationTypes, [key, "data_values", field, "display_name"], "Unknown");
                const { icon, style } = classificationTypeIcons[classification];
                const content = (
                    <span style={{ display: "flex", columnGap: 4 }}>
                        <div>
                            <strong>{_.get(classificationTypes, [key, "display_name"], "Unknown")}</strong>
                            {isIsolationField && (
                                <Tooltip
                                    overlayStyle={{ maxWidth: 500 }}
                                    title={
                                        <span>
                                            Classified by an authorised person and should be validated.
                                            <br />
                                            This data should not be relied upon for safety-critical decision making.
                                        </span>
                                    }>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        style={{ marginLeft: 2, textSize: 12 }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <FontAwesomeIcon
                            className="InfoIcon"
                            style={style}
                            icon={icon}
                        />
                    </span>
                );

                classifications.push(content);
            });

            infoElements.push(
                <div
                    className="IssueInfo"
                    key="signal-classifications">
                    <p className="IssueInfoText">
                        <div style={{ display: "flex", columnGap: 20 }}>{classifications}</div>
                    </p>
                </div>,
            );
        } else if (assetDataClass === "Limited Clearance") {
            const classifications = [];

            const classificationTypes = _.get(objectObservationDataTypes, "Limited Clearance", {});
            Object.keys(classificationTypes)
                .filter((key) => key === "lc_condition")
                .forEach((key) => {
                    const field = _.get(fields, key, null);

                    if (field !== null) {
                        let classification = _.get(classificationTypes, [key, "data_values", field, "display_name"], null);
                        if (classification === null && field === "indeterminate") {
                            classification = "Indeterminate";
                        }

                        const ragStatus = _.get(classificationTypes, [key, "data_values", field, "rag"], "indeterminate");

                        classifications.push(
                            <div style={{ display: "flex", alignItems: "center", columnGap: 8 }}>
                                <strong>{_.get(classificationTypes, [key, "display_name"], "Unknown")}</strong>:
                                <div style={{ display: "flex", alignItems: "center", columnGap: 6 }}>
                                    <span
                                        className={`rag-status-indicator ${ragStatus}`}
                                        style={{ "--rag-size": "12px" }}
                                    />
                                    {classification}
                                </div>
                            </div>,
                        );
                    }
                });

            const conditionConfidence = _.get(fields, "condition_confidence", null);
            if (conditionConfidence !== null && classifications.length > 0) {
                classifications.push(
                    <span>
                        <strong>Confidence</strong>: {conditionConfidence}
                    </span>,
                );
            }

            if (classifications.length) {
                infoElements.push(
                    <div
                        className="IssueInfo"
                        key="limited-clearance-classifications">
                        <p className="IssueInfoText">
                            <div style={{ display: "flex", columnGap: 20 }}>{classifications}</div>
                        </p>
                    </div>,
                );
            }
        }

        const propertyElements = [];

        const groundTruthData = assetData.ground_truth_data || {};
        const consensusData = assetData.consensus_data || {};

        const propertyData = createGroundTruthFields(groundTruthData, consensusData, assetData.class);

        if (
            ["Signal", "Tensioner", "Limited Clearance"].includes(assetDataClass) &&
            _.has(assetData, "route_position_chains") &&
            _.has(assetData, "route_position_trid")
        ) {
            const elrObj = ELRMileAndChain.from_fields("ELR Mile & Chain", "elr_position", {
                ELR: assetData.route_position_elr,
                MILE: 0,
                CHAIN: assetData.route_position_chains,
                TRACK: assetData.route_position_trid,
            });

            propertyData.push({ name: "Route Position:", value: elrObj.to_string(elrUnits), filedName: "routePosition", icon: {} });
        } else {
            const elr = _.get(assetData, "route_position_elr", null);
            if (elr) {
                propertyData.push({ name: "ELR", value: elr, fieldName: "elr", icon: {} });
            }
        }

        propertyData.forEach((property) => {
            let value = property.value;
            let reformattedSearchQuery = new AssetIDHelper(searchQuery).formatted_search_query();

            // replace the way we display Signal and "Structure IDs
            if (["Signal ID", "Structure ID"].includes(property.name)) {
                value = AssetIDHelper.from_asset_data(assetData).csv_to_slashes();
            }

            propertyElements.push(
                <tr className="PropertyRow">
                    <td className="PropertyKey">{property.name}</td>
                    <td className="PropertyValue">
                        {["ELR", "Signal ID", "Structure ID"].includes(property.name) ? (
                            <Highlighter
                                highlightClassName="ObcTextHighlight"
                                searchWords={searchQuery ? [reformattedSearchQuery, searchQuery] : []}
                                autoEscape={true}
                                textToHighlight={String(value)}
                            />
                        ) : (
                            value
                        )}
                    </td>
                    {property.status && !hasOnlyExternalObservations && (
                        <>
                            <td className="PropertyValue">
                                <StatusElement
                                    issue={assetData}
                                    groundTruthKey={property.fieldName}
                                    status={property.status}
                                />
                            </td>
                            <td className="PropertyValue">
                                <FontAwesomeIcon
                                    className={"EditIcon"}
                                    icon={faEdit}
                                    onClick={openGroundTruthModal}
                                />
                            </td>
                        </>
                    )}
                </tr>,
            );
        });

        let propertiesContainerElement = (
            <table className="PropertiesContainer">
                <tbody>{propertyElements}</tbody>
            </table>
        );

        infoElements.push(propertiesContainerElement);

        return infoElements;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetData, humanReadableDate, isSuperUser, objectObservationDataTypes, observationsWorkspaceWarning, searchQuery]);

    const superUserFields = useMemo(() => {
        const consensusData = assetData.consensus_data || {};
        const propertyData = createConcensusFields(consensusData);

        if (propertyData.length) {
            return (
                <div className="PropertiesContainer">
                    {propertyData.map((property) => {
                        let value = property.value;

                        // do not display too many digits after decimal point for coherence
                        if (property.name === "Coherence") {
                            value = String(_.floor(parseInt(property.value), 2)) + "%";
                        }

                        return (
                            <div className="Property">
                                <span className="key">{property.name}</span>
                                <span className="value">{value}</span>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return null;
    }, [assetData.consensus_data]);

    const viewingAdjustmentSwitches = useMemo(() => {
        return _.get(assetData, "class") === "Adjustment Switch Base Plate";
    }, [assetData]);

    const handleImageCallback = useCallback((imageSrc, setLoading, setImage) => {
        setLoading(false);
        if (imageSrc) {
            setImage(imageSrc);
        }
    }, []);

    useEffect(() => {
        if (!currentDashboard?.access_token) return;

        if (!assetImageSrc) {
            let url = _.get(assetData.summary_data, ["images", "thumbnail"]);
            if (viewingAdjustmentSwitches) {
                url = _.get(assetData.summary_data, ["fields", "last_left_rail", "thumbnail"], url);
            }

            getRawApiImage(url, currentDashboard.access_token, (imgSrc) => handleImageCallback(imgSrc, setImageLoading, setAssetImageSrc));
        }
        if (!assetImageSrcTwo && viewingAdjustmentSwitches) {
            const urlRight = _.get(assetData.summary_data, ["fields", "last_right_rail", "thumbnail"], null);
            if (urlRight) {
                getRawApiImage(urlRight, currentDashboard.access_token, (imgSrc) => handleImageCallback(imgSrc, setImageTwoLoading, setAssetImageSrcTwo));
            }
        } else {
            setImageTwoLoading(false);
        }
    }, [assetData.summary_data, assetImageSrc, assetImageSrcTwo, currentDashboard, viewingAdjustmentSwitches, handleImageCallback]);

    const image = useMemo(() => {
        if (imageLoading) {
            return (
                <OBCSpinner
                    size={50}
                    speed={3}
                    colorScheme={"mono"}
                />
            );
        }

        if (assetImageSrc) {
            return (
                <img
                    src={assetImageSrc}
                    alt="Asset"
                    crossOrigin="anonymous"
                    loading="lazy"
                />
            );
        }

        return null;
    }, [assetImageSrc, imageLoading]);

    const imageTwo = useMemo(() => {
        if (viewingAdjustmentSwitches) {
            if (imageTwoLoading) {
                return (
                    <OBCSpinner
                        size={50}
                        speed={3}
                        colorScheme={"mono"}
                    />
                );
            }

            if (assetImageSrcTwo) {
                return (
                    <img
                        src={assetImageSrcTwo}
                        alt="Asset"
                        crossOrigin="anonymous"
                        loading="lazy"
                        style={{ marginLeft: "5px" }}
                    />
                );
            }
        }
        return null;
    }, [assetImageSrcTwo, imageTwoLoading, viewingAdjustmentSwitches]);

    const groupsToUse = useMemo(() => {
        return assetGroups;
    }, [assetGroups]);

    const handleExternalObservationClick = (e) => {
        e.stopPropagation();
        setDisplayExternalAsset(!displayExternalAsset);

        if (!selected) {
            onIssueItemClick({});
        }
    };

    const incrementExternalAsset = (e) => {
        e.stopPropagation();
        if (externalAssetNumber < external_observations.length - 1) {
            setExternalAssetNumber(externalAssetNumber + 1);
        } else {
            setExternalAssetNumber(0);
        }
    };

    const decrementExternalAsset = (e) => {
        e.stopPropagation();
        if (externalAssetNumber === 0) {
            setExternalAssetNumber(external_observations.length - 1);
        } else {
            setExternalAssetNumber(externalAssetNumber - 1);
        }
    };

    const verifyExternalObservation = (e) => {
        e.stopPropagation();
        dispatch(
            reviewExternalObservation(
                external_observations[externalAssetNumber].id,
                external_observations[externalAssetNumber].review_status === 0,
                assetObservations,
            ),
        );
    };

    const trustLevel = _.get(assetData, "summary_data.fields.trust_level", null);

    const trustLevelInfo = useMemo(() => {
        const trustLevelText = `This asset record currently has trust level ${trustLevel}.`;

        if (trustLevel === 1) {
            return `${trustLevelText} This means the asset has been detected by AIVR, validated by a person and also matched to external record.`;
        } else if (trustLevel === 2) {
            return `${trustLevelText} This means the asset is an external record which has been verified by a person.`;
        } else if (trustLevel === 3) {
            return `${trustLevelText} This means the asset has been detected by AIVR and validated by a person, but not matched to any external record.`;
        } else if (trustLevel === 4) {
            return `${trustLevelText} This means the asset is an external record that has not yet been validated by a person.`;
        } else if (trustLevel === 5) {
            return `${trustLevelText} This means the asset has been detected by AIVR, and may have been matched to an external record, but neither AIVR nor the external record has been validated by a person.`;
        } else if (trustLevel === 6) {
            return `${trustLevelText} This means that there is a potential problem with this asset data (for example, there is conflicting data that also exists) and it should be used with caution.`;
        } else {
            return "";
        }
    }, [trustLevel]);

    const vegetationState = useMemo(() => {
        const vegetationState = _.get(assetData, ["summary_data", "fields", "vegetation_state"]);
        if (vegetationState && shouldShowVeg) {
            let vegetationContent = "Acceptable vegetation observed";
            let colour = "green";
            if (vegetationState === "vegetation_around_base") {
                vegetationContent = "Vegetation around the base";
                colour = "yellow";
            }
            if (vegetationState === "bad") {
                vegetationContent = "Unacceptable vegetation observed";
                colour = "red";
            }

            return (
                <Tippy
                    theme={"aivr"}
                    zIndex="1000"
                    delay="100"
                    arrow={true}
                    placement="left"
                    content={
                        <p
                            className="VegetationTooltip"
                            style={{ maxWidth: "300px", margin: "0px" }}>
                            {vegetationContent}
                        </p>
                    }>
                    <div className="Vegetation">
                        <p className="VegetationText">Vegetation</p>
                        <div
                            className="VegetationMarker"
                            style={{ backgroundColor: colour }}></div>
                    </div>
                </Tippy>
            );
        }

        return null;
    }, [assetData]);

    const adjustmentSwitchMeasurements = useMemo(() => {
        if (_.get(assetData, "class") === "Adjustment Switch Base Plate") {
            const lastLeftRail = _.get(assetData, ["summary_data", "fields", "last_left_rail"], {});
            const lastRightRail = _.get(assetData, ["summary_data", "fields", "last_right_rail"], {});

            const measurements = [];

            _.forEach([lastLeftRail, lastRightRail], (rail, index) => {
                if (!_.isEmpty(rail)) {
                    const { cut_out_1, cut_out_2, heel_offset_1, heel_offset_2, tip_offset_1, tip_offset_2 } = rail;
                    const railHeader = index === 0 ? "Left Rail" : "Right Rail";

                    measurements.push(
                        <div
                            key={index}
                            className="AdjustmentSwitchMeasurements">
                            <h3>{railHeader}</h3>
                            <div>
                                <span>Tip Support:</span> {tip_offset_1}mm & {tip_offset_2}mm
                            </div>
                            <div>
                                <span>Gap:</span> {cut_out_1}mm & {cut_out_2}mm
                            </div>
                            <div>
                                <span>Heel Support:</span> {heel_offset_1}mm & {heel_offset_2}mm
                            </div>
                        </div>,
                    );
                }
            });

            return <div className="AdjustmentSwitchContainer">{measurements}</div>;
        }

        return null;
    }, [assetData]);

    const externalAssetTooltip = () => {
        if (!_.isNil(external_observations) && external_observations.length > 0) {
            const verifyStatus = external_observations[externalAssetNumber].review_status ? (
                <button
                    className="ExternalAssetVerify"
                    onClick={verifyExternalObservation}>
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="ExternalAssetIconBlue"
                    />
                    Verified
                </button>
            ) : (
                <button
                    className="ExternalAssetVerify"
                    onClick={verifyExternalObservation}>
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="ExternalAssetIconGreen"
                    />
                    Verify observation
                </button>
            );

            return (
                <div className="ExternalAssetContainer">
                    <div className="ExternalAssetTooltip">
                        <button
                            className={`ExternalAssetButton${external_observations.length === 1 ? "Hidden" : ""}`}
                            onClick={decrementExternalAsset}>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <div className="ExternalAssetTooltipContainer">
                            <p className="ExternalAssetTooltipText">Asset ID: {external_observations[externalAssetNumber].asset_id}</p>
                            <p className="ExternalAssetTooltipText">{external_observations[externalAssetNumber].description}</p>
                        </div>
                        <button
                            className={`ExternalAssetButton${external_observations.length === 1 ? "Hidden" : ""}`}
                            onClick={incrementExternalAsset}>
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                    {verifyStatus}
                </div>
            );
        }
    };

    if (_.isEmpty(assetData)) {
        return (
            <AssetSkeleton
                assetRef={ref}
                minHeight={minHeight}
            />
        );
    }

    return (
        <div className="IssueListItemContainer">
            {!!notesModalOpen && (
                <Modal
                    key={`notes-modal-${assetData.id}`}
                    title="Edit Issue Notes"
                    onCancel={handleCancel}
                    onOk={saveNotes}
                    footer={[
                        <Button
                            key="cancel"
                            type="default"
                            onClick={handleCancel}>
                            Cancel
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            onClick={saveNotes}>
                            Save
                        </Button>,
                    ]}
                    visible={true}>
                    <div className="IssueNotesContainer">
                        <Input.TextArea
                            autoFocus
                            placeholder="Enter a note about this issue..."
                            maxLength={140}
                            rows={4}
                            className="NotesInput"
                            value={notes}
                            onPressEnter={saveNotes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                </Modal>
            )}

            <div
                className={"IssueListItem" + (selected ? " Open" : " Closed")}
                onClick={onIssueItemClick}
                style={{ minHeight }}
                ref={ref}>
                <div className="IssueHeader">
                    <div className="IssueLeftContainer">
                        <div className="IssueInfo">
                            <p className="IssueType">{_.get(groupsToUse, [assetData.issue_group_id, "display_name"], assetData.class) || assetData.class}</p>
                            {/* {issueStatus} */}
                        </div>

                        <div className="IssueInfo Note">
                            {assetData.note ? (
                                <p className="IssueNoteText">
                                    <Highlighter
                                        highlightClassName="ObcTextHighlight"
                                        searchWords={searchQuery ? searchQuery.split(" ") : []}
                                        autoEscape={true}
                                        textToHighlight={assetData.note}
                                    />
                                    {assetData.note_source && assetData.note_change_ts && (
                                        <Tooltip
                                            placement="bottom"
                                            mouseLeaveDelay={0}
                                            title={
                                                <span>
                                                    Last edited by {assetData.note_source} <br />
                                                    Date: {convertDate(assetData.note_change_ts)}
                                                </span>
                                            }>
                                            <span className="IssueAuthorAndDate">
                                                {assetData.note_source} - {moment.duration(moment().diff(moment(assetData.note_change_ts * 1000))).humanize()}{" "}
                                                ago
                                            </span>
                                        </Tooltip>
                                    )}
                                </p>
                            ) : assetData.note_source && assetData.note_change_ts ? (
                                <>
                                    <span className="IssueRemoved">[Removed]</span>
                                    <Tooltip
                                        placement="bottom"
                                        mouseLeaveDelay={0}
                                        title={
                                            <span>
                                                Note removed by {assetData.note_source} <br />
                                                Date: {convertDate(assetData.note_change_ts)}
                                            </span>
                                        }>
                                        <span className="IssueAuthorAndDate">
                                            {assetData.note_source} - {moment.duration(moment().diff(moment(assetData.note_change_ts * 1000))).humanize()} ago
                                        </span>
                                    </Tooltip>
                                </>
                            ) : (
                                <p className="IssueNoteText Empty">Enter a note</p>
                            )}
                            <FontAwesomeIcon
                                className={"InfoIcon Edit" + (assetData.note ? "" : " Empty")}
                                icon={faEdit}
                                onClick={openNotesModal}
                            />
                        </div>
                        {infoFields}
                        {adjustmentSwitchMeasurements}
                    </div>

                    <div className="IssueRightContainer">
                        <div className="IssueRightContainerTop">
                            {isSuperUser && superUserFields && (
                                <Tooltip title={superUserFields}>
                                    <div>
                                        <FontAwesomeIcon
                                            className="InfoIcon"
                                            icon={faInfoCircle}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                            <Tooltip
                                title={copyLinkTooltip}
                                onVisibleChange={(visible) => {
                                    if (!visible) {
                                        setCopyLinkTooltip("Copy link to asset");
                                    }
                                }}>
                                <div
                                    className="CopyLinkButton"
                                    onClick={onCopyLinkClicked}
                                    aria-label="Copy link to asset">
                                    <FontAwesomeIcon
                                        icon={faClipboard}
                                        className="CopyLinkIcon"
                                    />
                                </div>
                            </Tooltip>
                            <div className="IssueIdContainer">
                                {assetData.recalculating && (
                                    <Tooltip
                                        title={
                                            <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                                                <span>Recalculating Asset</span>
                                                <span>Some data may be out of date</span>
                                            </div>
                                        }>
                                        <div>
                                            <OBCSpinner
                                                size={20}
                                                color={"#e8dfff"}
                                                speed={3}
                                            />
                                        </div>
                                    </Tooltip>
                                )}

                                <div className="IssueID">
                                    <span>
                                        #
                                        <Highlighter
                                            highlightClassName="ObcTextHighlight"
                                            searchWords={searchQuery ? searchQuery.split(" ") : []}
                                            autoEscape={true}
                                            textToHighlight={String(assetData.id)}
                                        />
                                    </span>
                                </div>
                            </div>
                            {trustLevel && (
                                <Tippy
                                    theme={"aivr"}
                                    zIndex="1000"
                                    delay="100"
                                    arrow={true}
                                    placement="left"
                                    content={<p style={{ maxWidth: "300px", marginBottom: 0 }}>{trustLevelInfo}</p>}>
                                    <div className="AssetTrustLevelContainer">
                                        <p className="AssetTrustLevel">Level {trustLevel}</p>
                                    </div>
                                </Tippy>
                            )}
                            {vegetationState}
                        </div>

                        {assetData.has_external_observations && (
                            <div
                                className="ExternalAssetsContainer"
                                onClick={handleExternalObservationClick}>
                                <p className="ExternalAssets">External Data Match{positionDifference && `: ~${positionDifference}m`}</p>
                            </div>
                        )}
                        <Tippy
                            content={externalAssetTooltip()}
                            visible={displayExternalAsset}
                            interactive={true}
                            placement="bottom">
                            <div className="ExternalAssetPlacement" />
                        </Tippy>

                        <Tippy
                            arrow={true}
                            placement="bottom"
                            content={
                                <div className="IssueImageContainerToolTip">
                                    {image}
                                    {imageTwo}
                                </div>
                            }
                            theme="aivr"
                            zIndex="1000"
                            delay="300">
                            <div className="IssueImageContainer">
                                {image}
                                {imageTwo}
                            </div>
                        </Tippy>
                    </div>
                </div>
                <div className="IssueObservations">
                    <Divider className="Divider">Observations</Divider>
                    {observationItems}
                </div>
            </div>
        </div>
    );
};

export default AssetItem;
